import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store/store';
import { fetchListings } from '../../store/slices/listings';
import { fetchOffers } from '../../store/slices/offers';
import {
  selectMyBuyingListings,
  selectMySellingListings,
} from '../../store/selectors/listings';
import { selectOffers } from '../../store/selectors/offers';
import {
  SIGNUP_INTENT_CHANGED_EVENT,
  resolveSignupIntent,
} from '../../pages/HowItWorks/howItWorksStorage';
import { isCustomerType } from '../../utils/userType';
import { NavNarrowing, narrowNavForIntent } from './navModel';

/**
 * The rail items to hold back for the signed-in customer (see
 * `narrowNavForIntent`). The answer depends on listings and offers, so both
 * are fetched here when no page has done so yet.
 */
const useNavNarrowing = (): NavNarrowing => {
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const listingsLoaded = useSelector((state: RootState) => state.listings.loaded);
  const listingsLoading = useSelector((state: RootState) => state.listings.loading);
  const offersLoaded = useSelector((state: RootState) => state.offers.loaded);
  const offersLoading = useSelector((state: RootState) => state.offers.loading);
  const sellerListings = useSelector(selectMySellingListings);
  const buyerListings = useSelector(selectMyBuyingListings);
  const offers = useSelector(selectOffers);

  const userId = user?.id;
  const isCustomer = isCustomerType(user?.type);
  const [intent, setIntent] = useState(() => resolveSignupIntent(user));

  useEffect(() => {
    const reread = () => setIntent(resolveSignupIntent(user));
    reread();
    window.addEventListener(SIGNUP_INTENT_CHANGED_EVENT, reread);
    return () => window.removeEventListener(SIGNUP_INTENT_CHANGED_EVENT, reread);
  }, [userId, user?.howItWorksIntent]);

  const needsListings = isCustomer && intent !== null && intent !== 'BOTH';
  useEffect(() => {
    if (needsListings && !listingsLoaded && !listingsLoading) {
      dispatch(fetchListings({}));
    }
    if (needsListings && !offersLoaded && !offersLoading) {
      dispatch(fetchOffers());
    }
    // Fetch once per mount; a failed fetch leaves the full rail in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsListings, userId]);

  return narrowNavForIntent(user?.type, intent, {
    loaded: listingsLoaded && offersLoaded,
    sellerListings: sellerListings.length,
    buyerListings: buyerListings.length,
    offers: offers.length,
  });
};

export default useNavNarrowing;
